/**
 * Chatbot knowledge base — ingest text snippets, list documents, delete.
 *
 * (PDF/DOCX upload UI is queued; the backend `/admin/knowledge-base/ingest`
 * for files is a follow-up. For now editors paste text or copy from docs.)
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FileText as FileTextIcon, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/chatbot/knowledge-base/")({ component: KbPage });

interface Doc {
  id: number;
  title: string;
  source_type: "pdf" | "docx" | "md" | "url" | "text";
  source_url: string | null;
  status: "pending" | "indexed" | "failed" | "archived";
  ingested_at: number | null;
  version: number;
  updated_at: number;
}

function KbPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [ingestOpen, setIngestOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["kb", "list"],
    queryFn: () => api.get<{ items: Doc[] }>("/admin/knowledge-base"),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/knowledge-base/${id}`),
    onSuccess: () => { toast.success("Document removed"); qc.invalidateQueries({ queryKey: ["kb"] }); },
  });

  return (
    <div className="space-y-2">
      <PageHeader
        title="Chatbot knowledge base"
        description="Documents ingested into Vectorize. The chatbot retrieves from these to answer visitor queries."
        actions={can("knowledge_base", "create") && (
          <Button onClick={() => setIngestOpen(true)}><Plus className="h-4 w-4" /> Ingest</Button>
        )}
      />

      <Card>
        <CardContent className="px-0 pt-0 pb-0">
          {isLoading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (data?.items ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No documents indexed yet.</p>
          ) : (
            <ul className="divide-y">
              {data!.items.map((d) => (
                <li key={d.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{d.title}</p>
                      <Badge variant={d.status === "indexed" ? "success" : d.status === "pending" ? "warning" : d.status === "failed" ? "destructive" : "outline"}>{d.status}</Badge>
                      <Badge variant="outline" className="text-[9px]">{d.source_type}</Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {d.ingested_at ? `Ingested ${format(d.ingested_at * 1000, "MMM d, HH:mm")}` : "Pending"}
                      {" · "}v{d.version}
                      {d.source_url && <a href={d.source_url} target="_blank" rel="noreferrer" className="ml-2 underline">source</a>}
                    </p>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    disabled={!can("knowledge_base", "delete")}
                    onClick={() => { if (confirm(`Remove "${d.title}" from KB?`)) remove.mutate(d.id); }}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <IngestDialog open={ingestOpen} onOpenChange={setIngestOpen} />
    </div>
  );
}

const ingestSchema = z.object({
  title: z.string().min(1).max(200),
  text: z.string().min(50).max(2_000_000),
  source_url: z.string().url().or(z.literal("")).optional(),
});
type IngestValues = z.infer<typeof ingestSchema>;

// Accepted file extensions for client-side reading (read as plain text).
const ACCEPT_EXTS = ".md,.markdown,.mdx,.txt,text/plain,text/markdown";

function stripExt(name: string): string {
  return name.replace(/\.(md|markdown|mdx|txt)$/i, "");
}

function IngestDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (b: boolean) => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<IngestValues>({
    resolver: zodResolver(ingestSchema),
    defaultValues: { title: "", text: "", source_url: "" },
  });
  const ingest = useMutation({
    mutationFn: (v: IngestValues) => api.post<{ id: number; chunks: number }>("/admin/knowledge-base/ingest-text", {
      title: v.title, text: v.text, source_url: v.source_url || undefined,
    }),
    onSuccess: (r) => {
      toast.success(`Indexed ${r.chunks} chunks`);
      qc.invalidateQueries({ queryKey: ["kb"] });
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Ingest failed"),
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [loadedFile, setLoadedFile] = useState<{ name: string; size: number } | null>(null);
  const [batch, setBatch] = useState<{ current: number; total: number; currentName: string } | null>(null);

  function resetFilePicker() {
    if (fileInputRef.current) fileInputRef.current.value = "";
    setLoadedFile(null);
  }

  async function handleFilesSelected(files: FileList | null) {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);

    // Single file → pre-fill the form for review + manual submit.
    if (arr.length === 1) {
      const file = arr[0];
      try {
        const text = await file.text();
        setValue("title", stripExt(file.name), { shouldValidate: true });
        setValue("text", text, { shouldValidate: true });
        setLoadedFile({ name: file.name, size: file.size });
        toast.info(`Loaded "${file.name}" into the form. Review and click Index.`);
      } catch (e) {
        toast.error(`Could not read ${file.name}: ${e instanceof Error ? e.message : "unknown error"}`);
        resetFilePicker();
      }
      return;
    }

    // Multiple files → batch ingest, one document per file.
    setBatch({ current: 0, total: arr.length, currentName: "" });
    let success = 0;
    let failed = 0;
    for (let i = 0; i < arr.length; i++) {
      const file = arr[i];
      setBatch({ current: i + 1, total: arr.length, currentName: file.name });
      try {
        const text = await file.text();
        if (text.trim().length < 10) {
          toast.error(`Skipped "${file.name}" — too short (under 10 chars)`);
          failed++;
          continue;
        }
        await api.post("/admin/knowledge-base/ingest-text", {
          title: stripExt(file.name),
          text,
        });
        success++;
      } catch (e) {
        failed++;
        toast.error(`${file.name}: ${e instanceof Error ? e.message : "ingest failed"}`);
      }
    }
    setBatch(null);
    resetFilePicker();
    qc.invalidateQueries({ queryKey: ["kb"] });
    if (success > 0) {
      toast.success(`Indexed ${success} file${success === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
    } else if (failed > 0) {
      toast.error(`All ${failed} files failed`);
    }
    if (success > 0) onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); resetFilePicker(); } onOpenChange(o); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ingest into knowledge base</DialogTitle>
          <DialogDescription>
            Upload <code>.md</code> / <code>.txt</code> files <strong>or</strong> paste long-form text. The Worker chunks
            (~600 tokens / 100 overlap), embeds with Workers AI <code>bge-base-en-v1.5</code>, and upserts to Vectorize.
            Pick multiple files to batch-ingest one document per file.
          </DialogDescription>
        </DialogHeader>

        {/* File picker — single or multiple files */}
        <div className="rounded-md border border-dashed bg-muted/30 p-4">
          <input
            ref={fileInputRef}
            id="kb-file"
            type="file"
            accept={ACCEPT_EXTS}
            multiple
            className="hidden"
            onChange={(e) => handleFilesSelected(e.target.files)}
            disabled={!!batch}
          />
          {batch ? (
            <div className="flex items-center gap-3 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span>
                Indexing <strong>{batch.currentName}</strong> ({batch.current} of {batch.total})…
              </span>
            </div>
          ) : loadedFile ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-sm">
                <FileTextIcon className="h-4 w-4 text-muted-foreground" />
                <span>
                  Loaded <strong>{loadedFile.name}</strong>{" "}
                  <span className="text-muted-foreground">({(loadedFile.size / 1024).toFixed(1)} KB)</span>
                </span>
              </div>
              <Button type="button" size="sm" variant="ghost" onClick={() => { setValue("title", ""); setValue("text", ""); resetFilePicker(); }}>
                Clear
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Upload <code>.md</code> or <code>.txt</code> files — single file pre-fills the form below, multiple files batch-ingest.
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4" /> Choose files
              </Button>
            </div>
          )}
        </div>

        <form className="space-y-3" onSubmit={handleSubmit((v) => ingest.mutate(v))}>
          <div className="space-y-1.5">
            <Label htmlFor="kb-title">Title</Label>
            <Input id="kb-title" placeholder="Admissions FAQ — 2026 batch" {...register("title")} />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kb-source">Source URL (optional)</Label>
            <Input id="kb-source" placeholder="https://krct.ac.in/admissions" {...register("source_url")} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kb-text">Text</Label>
            <Textarea id="kb-text" rows={12} placeholder="Paste the source document text… (or use the upload above)" {...register("text")} />
            {errors.text && <p className="text-xs text-destructive">{errors.text.message}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={!!batch}>Cancel</Button>
            <Button type="submit" disabled={ingest.isPending || !!batch}>{ingest.isPending ? "Indexing…" : "Index"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
